// Thin wrapper over `navigator.credentials.*` that handles the binary
// encoding the WebAuthn protocol requires. The `webauthn-ruby` gem on
// the server expects the credential JSON in a specific shape — strings
// (id, rawId) base64-url-encoded, all `response.*` ArrayBuffers
// base64-url-encoded too. This helper does that round-trip so pages can
// stay declarative.
//
// `registerPasskey()` consumes the `passkeyOptions` prop the server
// rendered (output of `WebAuthn::Credential.options_for_create.as_json`)
// and POSTs the result to /portal/mfa/enroll/passkey.
// `verifyPasskey()` does the same for the assertion flow.

type WebAuthnOptions = Record<string, unknown> & {
  challenge: string
  user?: { id: string; name: string; displayName?: string }
  pubKeyCredParams?: Array<{ type: string; alg: number }>
  excludeCredentials?: Array<{ type: string; id: string }>
  allowCredentials?: Array<{ type: string; id: string }>
  rp?: { id?: string; name: string }
  timeout?: number
  authenticatorSelection?: Record<string, unknown>
  attestation?: string
  userVerification?: string
}

function base64UrlToArrayBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/")
  const padding = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4))
  const bin = atob(padded + padding)
  const buf = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) buf[i] = bin.charCodeAt(i)
  return buf.buffer
}

function arrayBufferToBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ""
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i])
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function decodeCreationOptions(opts: WebAuthnOptions): PublicKeyCredentialCreationOptions {
  const decoded: PublicKeyCredentialCreationOptions = {
    ...(opts as unknown as PublicKeyCredentialCreationOptions),
    challenge: base64UrlToArrayBuffer(opts.challenge),
    user: {
      ...(opts.user as PublicKeyCredentialUserEntity),
      id: base64UrlToArrayBuffer(opts.user!.id),
    },
  }
  if (opts.excludeCredentials) {
    decoded.excludeCredentials = opts.excludeCredentials.map((c) => ({
      type: c.type as PublicKeyCredentialType,
      id:   base64UrlToArrayBuffer(c.id),
    }))
  }
  return decoded
}

function decodeRequestOptions(opts: WebAuthnOptions): PublicKeyCredentialRequestOptions {
  const decoded: PublicKeyCredentialRequestOptions = {
    ...(opts as unknown as PublicKeyCredentialRequestOptions),
    challenge: base64UrlToArrayBuffer(opts.challenge),
  }
  if (opts.allowCredentials) {
    decoded.allowCredentials = opts.allowCredentials.map((c) => ({
      type: c.type as PublicKeyCredentialType,
      id:   base64UrlToArrayBuffer(c.id),
    }))
  }
  return decoded
}

function encodeAttestation(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAttestationResponse
  return {
    id:    cred.id,
    rawId: arrayBufferToBase64Url(cred.rawId),
    type:  cred.type,
    response: {
      clientDataJSON:    arrayBufferToBase64Url(r.clientDataJSON),
      attestationObject: arrayBufferToBase64Url(r.attestationObject),
    },
  }
}

function encodeAssertion(cred: PublicKeyCredential): Record<string, unknown> {
  const r = cred.response as AuthenticatorAssertionResponse
  return {
    id:    cred.id,
    rawId: arrayBufferToBase64Url(cred.rawId),
    type:  cred.type,
    response: {
      clientDataJSON:    arrayBufferToBase64Url(r.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(r.authenticatorData),
      signature:         arrayBufferToBase64Url(r.signature),
      userHandle:        r.userHandle ? arrayBufferToBase64Url(r.userHandle) : null,
    },
  }
}

export async function registerPasskey(opts: WebAuthnOptions): Promise<Record<string, unknown>> {
  const creation = decodeCreationOptions(opts)
  const cred = (await navigator.credentials.create({ publicKey: creation })) as PublicKeyCredential | null
  if (!cred) throw new Error("Passkey registration was cancelled.")
  return encodeAttestation(cred)
}

export async function verifyPasskey(opts: WebAuthnOptions): Promise<Record<string, unknown>> {
  const request = decodeRequestOptions(opts)
  const cred = (await navigator.credentials.get({ publicKey: request })) as PublicKeyCredential | null
  if (!cred) throw new Error("Passkey verification was cancelled.")
  return encodeAssertion(cred)
}
